export interface IHomePartnerLogo {
  id: string;
  file: string;
  alt: string;
  badge?: boolean;
}

export interface IHomeCorridorPreset {
  layout: 'collage' | 'row';
  partners: IHomePartnerLogo[];
  hasWallets: boolean;
}

export interface IHomeRateOption {
  currency: string;
  rate: number;
}

export type HomeEthnicityGroup =
  'afro-caribbean' | 'latin-american' | 'western-northern-european' | 'southern-european' | 'eastern-european' |
  'east-asian' | 'southeast-asian' | 'south-asian' | 'central-asian' | 'arab-gulf-levant' | 'north-african' |
  'west-african' | 'central-african' | 'horn-of-africa' | 'east-african' | 'southern-african' | 'christian-caucasus' | 'turkic-muslim';

export const HOME_HERO_ASSETS_PATH = 'assets/img/home/hero';
export const HOME_PARTNERS_ASSETS_PATH = 'assets/img/home/partners';
export const HOME_HERO_PROMO_CODE = 'HELLORIA';
export const HOME_PARTNERS_MARQUEE_MIN = 6;
export const HOME_PARTNERS_MARQUEE_MS_PER_LOGO = 6500;
export const HOME_HERO_RESTING_HEIGHT_PX = 356;
export const HOME_HERO_IMAGE_HEIGHT_PX = 449;
export const HOME_RATE_ROTATE_DWELL_MS = 5000;
export const HOME_RATE_ROTATE_ROLL_MS = 300;
export const HOME_RATE_ROTATE_SNAP_MS = 20;
export const HOME_RATE_SHIMMER_COLORS: string[] = ['#CAE88B', '#C7DAFF'];
export const HOME_RATE_SHIMMER_BASE_COLOR = '#0A0A0A';
export const HOME_RATE_SHIMMER_SWEEP_MS = 1900;
export const HOME_RATE_SHIMMER_PAUSE_MS = 3000;
export const HOME_RATE_SHIMMER_GLINT_SIGMA = 0.126;
export const HOME_RATE_SHIMMER_ZOOM = 1.18;
export const HOME_RATE_SHIMMER_GLINT_MARGIN = 0.4;

export const HOME_HERO_PORTRAIT_ISOS: string[] = ['CO', 'DO', 'EC', 'HT', 'IN', 'MA', 'MX', 'PE', 'PH', 'SV'];

export const HOME_HERO_WIDE_ISOS: string[] = ['AI', 'AU', 'BR', 'CA', 'CL', 'CV', 'FJ', 'KM', 'MG', 'MR', 'SC', 'SD', 'SR', 'US', 'VE'];

export const HOME_COUNTRY_TO_GROUP: { [iso: string]: HomeEthnicityGroup } = {
  AI: 'afro-caribbean', AG: 'afro-caribbean', HT: 'afro-caribbean', JM: 'afro-caribbean', GP: 'afro-caribbean', MQ: 'afro-caribbean',
  AR: 'latin-american', BO: 'latin-american', BR: 'latin-american', CL: 'latin-american', CO: 'latin-american', CR: 'latin-american',
  CU: 'latin-american', DO: 'latin-american', EC: 'latin-american', SV: 'latin-american', GT: 'latin-american', HN: 'latin-american',
  MX: 'latin-american', NI: 'latin-american', PA: 'latin-american', PY: 'latin-american', PE: 'latin-american', VE: 'latin-american',
  AT: 'western-northern-european', BE: 'western-northern-european', DK: 'western-northern-european', FI: 'western-northern-european',
  FR: 'western-northern-european', DE: 'western-northern-european', IS: 'western-northern-european', IE: 'western-northern-european',
  LU: 'western-northern-european', NL: 'western-northern-european', NO: 'western-northern-european', SE: 'western-northern-european',
  CH: 'western-northern-european', GB: 'western-northern-european',
  GR: 'southern-european', IT: 'southern-european', PT: 'southern-european', ES: 'southern-european',
  AL: 'eastern-european', BA: 'eastern-european', BG: 'eastern-european', CZ: 'eastern-european', XK: 'eastern-european', LT: 'eastern-european',
  MD: 'eastern-european', ME: 'eastern-european', MK: 'eastern-european', PL: 'eastern-european', RO: 'eastern-european', RS: 'eastern-european',
  UA: 'eastern-european',
  CN: 'east-asian', HK: 'east-asian', MO: 'east-asian', TW: 'east-asian', JP: 'east-asian', MN: 'east-asian',
  BN: 'southeast-asian', KH: 'southeast-asian', ID: 'southeast-asian', LA: 'southeast-asian', MY: 'southeast-asian', MM: 'southeast-asian',
  PH: 'southeast-asian', SG: 'southeast-asian', TH: 'southeast-asian', TL: 'southeast-asian', VN: 'southeast-asian',
  AF: 'south-asian', BD: 'south-asian', IN: 'south-asian', NP: 'south-asian', PK: 'south-asian', LK: 'south-asian', GY: 'south-asian', MU: 'south-asian',
  KZ: 'central-asian', KG: 'central-asian', TJ: 'central-asian', UZ: 'central-asian',
  BH: 'arab-gulf-levant', IQ: 'arab-gulf-levant', JO: 'arab-gulf-levant', KW: 'arab-gulf-levant', LB: 'arab-gulf-levant', OM: 'arab-gulf-levant',
  PS: 'arab-gulf-levant', QA: 'arab-gulf-levant', SA: 'arab-gulf-levant', SY: 'arab-gulf-levant', AE: 'arab-gulf-levant', YE: 'arab-gulf-levant',
  DZ: 'north-african', EG: 'north-african', MA: 'north-african', TN: 'north-african',
  BJ: 'west-african', BF: 'west-african', CI: 'west-african', GM: 'west-african', GH: 'west-african', GN: 'west-african', GW: 'west-african',
  LR: 'west-african', ML: 'west-african', NE: 'west-african', NG: 'west-african', SN: 'west-african', SL: 'west-african', TG: 'west-african',
  AO: 'central-african', CM: 'central-african', CF: 'central-african', TD: 'central-african', CG: 'central-african', CD: 'central-african',
  GQ: 'central-african', GA: 'central-african',
  DJ: 'horn-of-africa', ER: 'horn-of-africa', ET: 'horn-of-africa', SO: 'horn-of-africa',
  KE: 'east-african', RW: 'east-african', TZ: 'east-african', UG: 'east-african',
  BW: 'southern-african', SZ: 'southern-african', MZ: 'southern-african', NA: 'southern-african', ZA: 'southern-african', ZM: 'southern-african',
  ZW: 'southern-african',
  AM: 'christian-caucasus', GE: 'christian-caucasus',
  TR: 'turkic-muslim', AZ: 'turkic-muslim'
};



// WEBPACK FOOTER //
// ./src/model/types/home-revamp-types.ts